import { all, takeLatest, call, put } from 'redux-saga/effects';
import Axios, { AxiosResponse } from 'axios';
import {
  saveUserProfileSuccess,
  saveUserProfileFailure,
  requestUserLinkingSuccess,
  requestUserLinkingFailure,
  verifyUserLinkingSuccess,
  verifyUserLinkingFailure,
  confirmUserLinkingSuccess,
  confirmUserLinkingFailure,
  setEmailLinkingSuccess,
  setEmailLinkingFailure,
  importEnquiriesSuccess,
  importEnquiriesFailure,
  fetchImportEnquiriesStatusSuccess,
  fetchImportEnquiriesStatusFailure
} from './actions';
import { OnboardingActionTypes } from './types';
import {
  requestUserLinking,
  verifyUserLinking,
  confirmUserLinking,
  setEmailLinking,
  importEnquiries
} from 'services/sentinel.service';
import { getSystemJob } from 'services/system-job.service';
import { updateProfile } from 'services/user.service';

function* handleSaveUserProfile(request: any) {
  try {
    const response: AxiosResponse = yield updateProfile(request.payload);
    if (response) {
      yield put(saveUserProfileSuccess(response.data));
    } else {
      yield put(saveUserProfileFailure('Unable to save user profile'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(saveUserProfileFailure(errMsg || 'Unable to save user profile'));
  }
}

function* handleRequestUserLinking(request: any) {
  try {
    const response: AxiosResponse = yield requestUserLinking(request.payload);
    if (response) {
      yield put(requestUserLinkingSuccess(response.data));
    } else {
      yield put(requestUserLinkingFailure('Unable to request user linking'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(
      requestUserLinkingFailure(errMsg || 'Unable to request user linking')
    );
  }
}

function* handleVerifyUserLinking(request: any) {
  try {
    const response: AxiosResponse = yield verifyUserLinking(request.payload);
    if (response) {
      yield put(verifyUserLinkingSuccess(response.data));
    } else {
      yield put(verifyUserLinkingFailure('Unable to verify user linking'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.error?.message
      : undefined;
    yield put(
      verifyUserLinkingFailure(errMsg || 'Unable to verify user linking')
    );
  }
}

function* handleConfirmUserLinking(request: any) {
  try {
    const response: AxiosResponse = yield confirmUserLinking(request.payload);
    if (response) {
      yield put(confirmUserLinkingSuccess(response.data));
    } else {
      yield put(confirmUserLinkingFailure('Unable to confirm user linking'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.error?.message
      : undefined;
    yield put(
      confirmUserLinkingFailure(errMsg || 'Unable to confirm user linking')
    );
  }
}

function* handleSetEmailLinking(request: any) {
  try {
    const response: AxiosResponse = yield setEmailLinking(request.payload);
    if (response) {
      yield put(setEmailLinkingSuccess(response.data));
    } else {
      yield put(setEmailLinkingFailure('Unable to set email link'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(setEmailLinkingFailure(errMsg || 'Unable to set email link'));
  }
}

function* handleImportEnquiries(request: any) {
  try {
    const response: AxiosResponse = yield importEnquiries(request.payload);
    if (response) {
      yield put(importEnquiriesSuccess(response.data));
    } else {
      yield put(importEnquiriesFailure('Unable to import enquiries'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(importEnquiriesFailure(errMsg || 'Unable to import enquiries'));
  }
}

function* handleImportStatus(request: any) {
  try {
    const response: AxiosResponse = yield getSystemJob(request.payload);
    if (response) {
      yield put(fetchImportEnquiriesStatusSuccess(response.data));
    } else {
      yield put(
        fetchImportEnquiriesStatusFailure(
          'Unable to get import enquiries status'
        )
      );
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(
      fetchImportEnquiriesStatusFailure(
        errMsg || 'Unable to get import enquiries status'
      )
    );
  }
}

function* watchSaveUserProfile() {
  yield takeLatest(
    OnboardingActionTypes.SAVE_USER_PROFILE_START,
    handleSaveUserProfile
  );
}

function* watchRequestUserLinking() {
  yield takeLatest(
    OnboardingActionTypes.REQUEST_USER_LINKING_START,
    handleRequestUserLinking
  );
}

function* watchVerifyUserLinking() {
  yield takeLatest(
    OnboardingActionTypes.VERIFY_USER_LINKING_START,
    handleVerifyUserLinking
  );
}

function* watchConfirmUserLinking() {
  yield takeLatest(
    OnboardingActionTypes.CONFIRM_USER_LINKING_START,
    handleConfirmUserLinking
  );
}

function* watchSetEmailLinking() {
  yield takeLatest(
    OnboardingActionTypes.SET_EMAIL_LINKING_START,
    handleSetEmailLinking
  );
}

function* watchImportEnquiries() {
  yield takeLatest(
    OnboardingActionTypes.IMPORT_ENQUIRIES_START,
    handleImportEnquiries
  );
}

function* watchImportStatus() {
  yield takeLatest(
    OnboardingActionTypes.FETCH_IMPORT_ENQUIRIES_STATUS_START,
    handleImportStatus
  );
}

function* sagas() {
  yield all([
    call(watchSaveUserProfile),
    call(watchRequestUserLinking),
    call(watchVerifyUserLinking),
    call(watchConfirmUserLinking),
    call(watchSetEmailLinking),
    call(watchImportEnquiries),
    call(watchImportStatus)
  ]);
}

export default sagas;
